import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as gatingApi from './api'
import type { StageRequirementRequest } from './types'

export const gatingKeys = {
  gate: (contractId: number) => ['gating', 'gate', contractId] as const,
  requirementsByConfiguration: (configurationCode: string, stage?: string) =>
    ['gating', 'requirements', 'product', configurationCode, stage ?? 'all'] as const,
  requirementsByProduct: (productCode: string, stage?: string) =>
    ['gating', 'requirements', 'element', productCode, stage ?? 'all'] as const,
}

export function useContractGate(contractId: number) {
  return useQuery({
    queryKey: gatingKeys.gate(contractId),
    queryFn: () => gatingApi.getContractGate(contractId),
  })
}

export function useStageRequirementsByConfiguration(configurationCode: string, stage?: string) {
  return useQuery({
    queryKey: gatingKeys.requirementsByConfiguration(configurationCode, stage),
    queryFn: () => gatingApi.listRequirementsByConfiguration(configurationCode, stage),
    enabled: !!configurationCode,
  })
}

export function useCreateRequirement(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StageRequirementRequest) => gatingApi.createRequirement(configurationCode, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gating', 'requirements'] })
      qc.invalidateQueries({ queryKey: ['gating', 'gate'] })
    },
  })
}

export function useProductStageRequirements(productCode: string, stage?: string) {
  return useQuery({
    queryKey: gatingKeys.requirementsByProduct(productCode, stage),
    queryFn: () => gatingApi.listRequirementsByProduct(productCode, stage),
    enabled: !!productCode,
  })
}

export function useCreateProductRequirement(productCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StageRequirementRequest) => gatingApi.createProductRequirement(productCode, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gating', 'requirements'] })
      qc.invalidateQueries({ queryKey: ['contract-elements'] })
    },
  })
}

export function useDeleteRequirement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => gatingApi.deleteRequirement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gating', 'requirements'] })
      qc.invalidateQueries({ queryKey: ['gating', 'gate'] })
      qc.invalidateQueries({ queryKey: ['contract-elements'] })
    },
  })
}
